import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { formatDate } from '../../utils/dateUtils';
import { useCurrency } from '../../hooks/useCurrency';

interface VehicleSummaryPanelProps {
  vehicle: Vehicle;
  reportUsers: Record<string, { displayName: string }>;
  onEditVehicle: (vehicle: Vehicle) => void;
  onDeleteVehicle: (vehicleId: string) => void;
}

const VehicleSummaryPanel: React.FC<VehicleSummaryPanelProps> = ({
  vehicle,
  reportUsers,
  onEditVehicle,
  onDeleteVehicle
}) => {
  const { t } = useTranslation();
  const { format: formatCurrency, loading: currencyLoading } = useCurrency(vehicle.organizationId);

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      {/* Basic Info */}
      <Typography variant="h6" gutterBottom>
        {t('vehicle.details.sections.basicInfo')}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1">
          {`${vehicle.make || '-'} ${vehicle.model || '-'} (${vehicle.year || '-'})`}
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {vehicle.licensePlate || '-'}
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {t('vehicle.firstRegistrationDate')}: {vehicle.firstRegistrationDate ? formatDate(vehicle.firstRegistrationDate.toDate()) : '-'}
        </Typography>
        <Typography color="text.secondary">
          {t('vehicle.status.title')}: {t(`vehicle.status.${vehicle.status || 'active'}`)}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onEditVehicle(vehicle)}
            fullWidth
          >
            {t('common.edit')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => {
              if (window.confirm(t('common.confirm'))) {
                onDeleteVehicle(vehicle.id);
              }
            }}
            fullWidth
          >
            {t('common.delete')}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Assignment Info */}
      <Typography variant="subtitle2" gutterBottom>
        {t('vehicle.assignment.currentlyAssigned')}
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        {vehicle.currentAssignment 
          ? reportUsers?.[vehicle.currentAssignment.userId]?.displayName
          : t('vehicle.details.messages.notAssigned')}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Current Mileage */}
      <Typography variant="subtitle2" gutterBottom>
        {t('vehicle.mileage.current')}
      </Typography>
      <Typography gutterBottom>
        {(vehicle.currentMileage || 0).toLocaleString()} {t('vehicle.mileage.km')}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Insurance Info */}
      <Typography variant="subtitle2" gutterBottom>
        {t('vehicle.details.fields.supplier')}
      </Typography>
      <Typography gutterBottom>
        {vehicle.insurance?.provider || '-'}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('vehicle.export.headers.policyNumber')}: {vehicle.insurance?.policyNumber || '-'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('vehicle.details.fields.dueDate')}: {vehicle.insurance?.expirationDate ? formatDate(vehicle.insurance.expirationDate.toDate()) : '-'}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Financial Info */}
      <Typography variant="subtitle2" gutterBottom>
        {t('vehicle.costs.title')}
      </Typography>
      {currencyLoading ? (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t('vehicle.details.fields.totalRepairCosts')}: {formatCurrency(vehicle.totalRepairCosts || 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('vehicle.details.fields.totalFuelCosts')}: {formatCurrency(vehicle.totalFuelCosts || 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('vehicle.details.fields.annualMaintenance')}: {formatCurrency(vehicle.annualMaintenanceCost || 0)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default VehicleSummaryPanel;